import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { api, fileUrl } from "@/lib/api";
import { Sparkles, Ruler, Truck, ShieldCheck, ArrowUpRight, Clock, MapPin, Image as ImageIcon, Layers, Megaphone, Palette } from "lucide-react";
import { COMPANY, LOGO_URL, LOGO_HERO_URL } from "@/lib/brand";
import { useScrollReveal } from "@/lib/useScrollReveal";

const features = [
  { icon: Ruler, title: "Any Size", desc: "Custom dimensions in feet or inches with live pricing." },
  { icon: Sparkles, title: "AI Design", desc: "Generate banner artwork in seconds with Gemini AI." },
  { icon: Truck, title: "Pickup or Deliver", desc: "Pick up in Naga or ship CamSur-wide." },
  { icon: ShieldCheck, title: "300 DPI Ready", desc: "Print-grade resolution, vivid outdoor inks." },
];

const heroCategories = [
  { label: "Tarpaulin", icon: ImageIcon, to: "/products" },
  { label: "Stickers", icon: Layers, to: "/products" },
  { label: "AI Design", icon: Sparkles, to: "/ai-designer" },
  { label: "Signage", icon: Megaphone, to: "/products" },
];

const HomePage = () => {
  const [topServices, setTopServices] = useState([]);
  useEffect(() => {
    api.get("/print-services/top-by-orders?limit=4").then(({ data }) => setTopServices(data || []))
      .catch(() => setTopServices([]));
  }, []);
  // Re-run scroll-reveal once the async top-services list is populated,
  // otherwise the freshly-mounted cards stay at opacity:0.
  useScrollReveal([topServices.length]);
  return (
  <div data-testid="home-page">
    {/* HERO — Cronza dark immersive with print-shop background */}
    <section className="hero-printshop-bg relative overflow-hidden -mt-24 pt-28 lg:pt-32 pb-24 lg:pb-32 min-h-[100vh]" data-testid="home-hero">
      <div className="hero-printshop-overlay" aria-hidden="true" />
      {/* Floating hex decorations — top-left & bottom-right */}
      <div className="hidden md:block absolute left-[6%] top-[28%] cronza-float pointer-events-none" aria-hidden="true">
        <div className="cronza-hex" style={{ "--hex-bg": "#0E2147" }}>
          <span className="cronza-hex-label">{heroCategories[0].label}</span>
        </div>
      </div>
      <div className="hidden md:block absolute left-[12%] bottom-[12%] cronza-float-delay pointer-events-none" aria-hidden="true">
        <div className="cronza-hex" style={{ "--hex-bg": "#0E2147", width: "200px", height: "220px" }}>
          <span className="cronza-hex-label" style={{ fontSize: "1.15rem" }}>{heroCategories[1].label}</span>
        </div>
      </div>
      <div className="hidden md:block absolute right-[8%] top-[24%] cronza-float-delay pointer-events-none" aria-hidden="true">
        <div className="cronza-hex" style={{ "--hex-bg": "#0E2147" }}>
          <span className="cronza-hex-label">{heroCategories[2].label}</span>
        </div>
      </div>
      <div className="hidden md:block absolute right-[14%] bottom-[14%] cronza-float-slow pointer-events-none" aria-hidden="true">
        <div className="cronza-hex" style={{ "--hex-bg": "#0E2147", width: "200px", height: "220px" }}>
          <span className="cronza-hex-label" style={{ fontSize: "1.15rem" }}>{heroCategories[3].label}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <img src={LOGO_HERO_URL} alt="Print Ad Venture"
          data-testid="hero-logo"
          className="reveal mx-auto w-[min(1100px,92vw)] max-h-[58vh] object-contain drop-shadow-[0_22px_55px_rgba(6,182,212,0.40)]" />

        <h2 className="reveal reveal-delay-2 mt-6 text-white text-lg lg:text-2xl max-w-2xl mx-auto font-medium drop-shadow-lg" data-testid="hero-subtitle">
          Your ultimate gateway to custom tarpaulin banners, large-format prints,
          AI-generated artwork, and the future of brand visibility.
        </h2>

        <div className="reveal reveal-delay-3 mt-8 flex justify-center px-4">
          <div className="cronza-pill-input max-w-md w-full">
            <input type="text" placeholder="What do you want to print today?"
              data-testid="hero-search-input"
              onKeyDown={(e) => { if (e.key === "Enter") window.location.assign("/order"); }} />
            <Link to="/order" className="pav-pill-btn text-sm" data-testid="hero-search-cta">
              Start
              <span className="pav-pill-arrow"><ArrowUpRight className="h-4 w-4" /></span>
            </Link>
          </div>
        </div>

        <div className="reveal reveal-delay-4 mt-7 flex flex-wrap justify-center gap-x-7 gap-y-2 text-xs uppercase tracking-[0.18em] text-white/55">
          <span className="inline-flex items-center gap-1.5"><Clock className="h-3 w-3 text-[#06B6D4]" /> {COMPANY.hours}</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="h-3 w-3 text-[#06B6D4]" /> Tinago, Naga City</span>
        </div>
      </div>
    </section>

    {/* PRODUCT GRID (Cronza Articles grid) */}
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="home-products-section">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <span className="reveal cronza-eyebrow">Order Print Online</span>
          <h2 className="reveal reveal-delay-1 font-display text-3xl sm:text-5xl font-extrabold text-[#0B1E3F] mt-3 tracking-tight">
            Stay ahead with our most-ordered services.
          </h2>
        </div>
        <Link to="/services" className="reveal pav-pill-btn pav-pill-btn-dark text-sm" data-testid="products-see-all">
          Browse all
          <span className="pav-pill-arrow"><ArrowUpRight className="h-4 w-4" /></span>
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {(topServices.length > 0 ? topServices : []).map((s, idx) => (
          <Link to={`/order?service=${s.service_id}`} key={s.service_id}
            onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
            className={`cronza-card group block reveal reveal-delay-${(idx % 4) + 1}`}
            data-testid={`home-top-service-${s.service_id}`}>
            <div className="aspect-[4/3] overflow-hidden relative bg-[#F4F6FB]">
              {s.cover_photo || s.photos?.[0] ? (
                <img src={fileUrl(s.cover_photo || s.photos[0])} alt={s.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-neutral-300 text-xs">No image</div>
              )}
              {idx === 0 && s.order_count > 0 && <span className="cronza-badge absolute top-3 left-3">Bestseller</span>}
              {idx > 0 && idx < 3 && s.order_count > 0 && <span className="cronza-badge absolute top-3 left-3">Popular</span>}
            </div>
            <div className="p-5">
              <h3 className="font-display font-bold text-[#0B1E3F] text-lg leading-tight">{s.name}</h3>
              <p className="text-sm text-neutral-500 mt-1.5">
                {s.base_price ? `From ₱${Number(s.base_price).toLocaleString()} ${s.unit || ""}` : (s.short_description || "")}
              </p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#06B6D4] group-hover:gap-2.5 transition-all">
                Order now <ArrowUpRight className="h-3.5 w-3.5" />
              </div>
            </div>
          </Link>
        ))}
        {topServices.length === 0 && (
          // Skeleton placeholders while loading
          [0, 1, 2, 3].map((i) => (
            <div key={i} className="cronza-card animate-pulse">
              <div className="aspect-[4/3] bg-[#F4F6FB]" />
              <div className="p-5 space-y-2">
                <div className="h-4 bg-[#F4F6FB] rounded w-2/3" />
                <div className="h-3 bg-[#F4F6FB] rounded w-1/2" />
              </div>
            </div>
          ))
        )}
      </div>
    </section>

    {/* FEATURE STRIP */}
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {features.map((f, idx) => (
          <div key={f.title}
            className={`pav-card reveal reveal-delay-${idx + 1}`}
            data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="h-11 w-11 rounded-2xl bg-[#06B6D4]/10 flex items-center justify-center mb-4">
              <f.icon className="h-5 w-5 text-[#06B6D4]" />
            </div>
            <h3 className="font-display font-bold text-lg text-[#0B1E3F]">{f.title}</h3>
            <p className="text-sm text-neutral-600 mt-1.5">{f.desc}</p>
          </div>
        ))}
      </div>
    </section>

    {/* COVERAGE / Big statement (Cronza "Covering the global crypto ecosystem...") */}
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
      <span className="reveal cronza-eyebrow justify-center">Why Print Ad Venture</span>
      <h2 className="reveal reveal-delay-1 font-display text-3xl sm:text-5xl font-extrabold text-[#0B1E3F] mt-4 max-w-4xl mx-auto leading-[1.1]">
        Covering the Bicol region with crisp prints, accurate color, and a deep commitment to deadlines.
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
        {[
          { title: "Trusted production", desc: "300 DPI heat-resistant inks, professionally trimmed and eyeleted — every order, every time.", icon: ShieldCheck },
          { title: "Full coverage", desc: "From 2-foot tarps to 30-foot mega banners, business cards, signage and roll-ups — we do it all.", icon: Palette },
          { title: "Honest pricing", desc: "Live per-square-foot calculator. No hidden setup fees, no surprises at pickup.", icon: Truck },
        ].map((b, i) => (
          <div key={b.title} className={`reveal reveal-delay-${i + 1} pav-card text-left`}>
            <div className="h-12 w-12 rounded-2xl bg-[#0B1E3F] flex items-center justify-center mb-4">
              <b.icon className="h-6 w-6 text-[#06B6D4]" />
            </div>
            <h3 className="font-display font-bold text-xl text-[#0B1E3F]">{b.title}</h3>
            <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{b.desc}</p>
          </div>
        ))}
      </div>
    </section>

    {/* CTA BAND (Cronza newsletter band, but for orders) */}
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="cronza-bg-dots rounded-[2rem] p-10 lg:p-16 relative overflow-hidden" data-testid="home-cta-band">
        <div className="absolute -top-10 -right-10 cronza-hex cronza-float opacity-50 hidden lg:block" aria-hidden="true">
          <Sparkles className="h-8 w-8 text-[#06B6D4]" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <span className="cronza-badge cronza-badge-dark">Get started in minutes</span>
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold text-white mt-5 leading-[1.1]">
            Need it printed ASAP?
          </h2>
          <p className="text-white/70 mt-4 max-w-xl mx-auto">
            Upload your design before 12 NN and pick it up at our Tinago shop the same day or delivered at your doorstep.
            Real customer service over chat, phone, or in person.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/order" className="pav-pill-btn" data-testid="cta-order">
              Order now
              <span className="pav-pill-arrow"><ArrowUpRight className="h-4 w-4" /></span>
            </Link>
            <a href={`tel:${COMPANY.phone}`} className="pav-pill-btn pav-pill-btn-ghost" data-testid="cta-call">
              {COMPANY.phone}
              <span className="pav-pill-arrow"><ArrowUpRight className="h-4 w-4" /></span>
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
  );
};

export default HomePage;
